import { shallowRef, type ShallowRef } from 'vue'

import type {
  ChatColorThemeInitResult,
  ChatColorThemeResolutionSource,
  ChatColorThemeValue,
} from '~/types/ChatColorTheme'

/**
 * Préférence de thème couleur pour la zone chat `/discuter`.
 * Cookie, attribut `data-theme` sur `<html>` (anti-FOUC) et état réactif partagé.
 */
export class ChatColorThemePreference {
  static readonly cookieName: string = 'prepeers_chat_theme'
  static readonly themeAttribute: string = 'data-theme'

  private static readonly cookieMaxAgeSeconds: number = 60 * 60 * 24 * 365
  private static readonly allowedThemeValues: readonly ChatColorThemeValue[] = [
    'light',
    'dark',
  ] as const

  readonly activeTheme: ShallowRef<ChatColorThemeValue>

  private hasInitializedOnClient: boolean = false

  constructor() {
    this.activeTheme = shallowRef<ChatColorThemeValue>('dark')
  }

  /**
   * Lit la préférence système (`prefers-color-scheme`).
   * @returns {ChatColorThemeValue} `dark` si le système est en mode sombre, sinon `light`.
   */
  readSystemPreference(): ChatColorThemeValue {
    if (!import.meta.client) return 'dark'
    const prefersDark: boolean = window.matchMedia('(prefers-color-scheme: dark)').matches
    return prefersDark ? 'dark' : 'light'
  }

  /**
   * Lit le cookie de préférence utilisateur.
   * @returns {ChatColorThemeValue | null} Thème stocké, ou `null` si absent / invalide.
   */
  readStoredPreference(): ChatColorThemeValue | null {
    const stored: string | null = this.readCookie(ChatColorThemePreference.cookieName)
    return this.isChatColorThemeValue(stored) ? stored : null
  }

  /**
   * Résout le thème initial : cookie prioritaire, sinon système.
   * @returns {{ theme: ChatColorThemeValue; source: ChatColorThemeResolutionSource }} Thème et origine.
   */
  resolveInitialTheme(): {
    theme: ChatColorThemeValue
    source: ChatColorThemeResolutionSource
  } {
    const stored: ChatColorThemeValue | null = this.readStoredPreference()
    if (stored !== null) {
      return { theme: stored, source: 'cookie' }
    }
    return { theme: this.readSystemPreference(), source: 'system' }
  }

  /**
   * Persiste la préférence utilisateur dans un cookie.
   * @param {ChatColorThemeValue} theme - Thème choisi.
   * @returns {void}
   */
  persistPreference(theme: ChatColorThemeValue): void {
    if (!ChatColorThemePreference.allowedThemeValues.includes(theme)) return
    this.writeCookie(ChatColorThemePreference.cookieName, theme)
  }

  /**
   * Applique `data-theme` sur `<html>` pour l'anti-FOUC et les sélecteurs CSS chat.
   * @param {ChatColorThemeValue} theme - Thème à appliquer.
   * @returns {void}
   */
  applyToDom(theme: ChatColorThemeValue): void {
    if (!import.meta.client) return
    document.documentElement.setAttribute(ChatColorThemePreference.themeAttribute, theme)
  }

  /**
   * Retire l'attribut thème du document (sortie du layout chat).
   * @returns {void}
   */
  clearFromDom(): void {
    if (!import.meta.client) return
    document.documentElement.removeAttribute(ChatColorThemePreference.themeAttribute)
  }

  /**
   * Synchronise cookie, DOM et état réactif avec la préférence résolue.
   * @param {ChatColorThemeValue} theme - Thème à appliquer.
   * @returns {void}
   */
  setTheme(theme: ChatColorThemeValue): void {
    this.activeTheme.value = theme
    this.persistPreference(theme)
    this.applyToDom(theme)
  }

  /**
   * Bascule entre `light` et `dark`.
   * @returns {ChatColorThemeValue} Thème appliqué après bascule.
   */
  toggleTheme(): ChatColorThemeValue {
    const nextTheme: ChatColorThemeValue = this.activeTheme.value === 'dark' ? 'light' : 'dark'
    this.setTheme(nextTheme)
    return nextTheme
  }

  /**
   * Initialise le thème côté client (après le script anti-FOUC du layout).
   * @returns {ChatColorThemeInitResult | null} Résolution initiale, ou `null` hors client.
   */
  initOnClient(): ChatColorThemeInitResult | null {
    if (!import.meta.client) return null

    const { theme, source } = this.resolveInitialTheme()
    this.setTheme(theme)

    const isFirstClientInit: boolean = !this.hasInitializedOnClient
    this.hasInitializedOnClient = true

    return { theme, source, isFirstClientInit }
  }

  /**
   * Script inline exécuté avant le premier paint sur le layout chat (anti-FOUC).
   * @returns {string} Code JavaScript à injecter dans `useHead`.
   */
  static buildBootstrapScript(): string {
    return `(function(){try{var n=${JSON.stringify(ChatColorThemePreference.cookieName)};var a=${JSON.stringify(ChatColorThemePreference.themeAttribute)};function rc(name){var p=name+"=";var parts=document.cookie.split("; ");for(var i=0;i<parts.length;i++){if(parts[i].indexOf(p)===0){return decodeURIComponent(parts[i].slice(p.length));}}return null;}function sys(){return window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";}var s=rc(n);var t=s==="light"||s==="dark"?s:sys();document.documentElement.setAttribute(a,t);}catch(e){}})();`
  }

  /**
   * Indique si une valeur est un thème chat valide.
   * @param {string | null | undefined} value - Valeur lue (cookie, attribut DOM…).
   * @returns {value is ChatColorThemeValue} `true` si `light` ou `dark`.
   */
  private isChatColorThemeValue(value: string | null | undefined): value is ChatColorThemeValue {
    return value === 'light' || value === 'dark'
  }

  /**
   * Lit la valeur d'un cookie côté client.
   * @param {string} name - Nom du cookie.
   * @returns {string | null} Valeur décodée, ou `null` si absent ou hors client.
   */
  private readCookie(name: string): string | null {
    if (!import.meta.client) return null
    const prefix: string = `${name}=`
    const match: string | undefined = document.cookie
      .split('; ')
      .find((row: string) => row.startsWith(prefix))
    return match === undefined ? null : decodeURIComponent(match.slice(prefix.length))
  }

  /**
   * Écrit un cookie de préférence thème chat (1 an, SameSite=Lax).
   * @param {string} name - Nom du cookie.
   * @param {string} value - Valeur (`light` | `dark`).
   * @returns {void}
   */
  private writeCookie(name: string, value: string): void {
    if (!import.meta.client) return
    const attributes: string[] = [
      `${name}=${encodeURIComponent(value)}`,
      'path=/',
      `max-age=${ChatColorThemePreference.cookieMaxAgeSeconds}`,
      'SameSite=Lax',
    ]
    if (!import.meta.dev) attributes.push('Secure')
    document.cookie = attributes.join('; ')
  }
}

export const chatColorThemePreference: ChatColorThemePreference = new ChatColorThemePreference()
